import { useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { apiRequest } from '../Services/Common/ApiRequest';
import Global from '../Services/Common/Global';
import { mapParamsBySchema } from '../util';
import { handleQuerySuccess } from './useQuriesSuccess';
import { PLA_BASIC_Schema } from '../Scripts/BasicInfo/Plant/PLA_BASIC/PLA_BASIC_Schema';
import { MEM_BASIC_Schema } from '../Scripts/BasicInfo/Member/MEM_BASIC/MEM_BASIC_Schema';
import { DEP_BASIC_Schema } from '../Scripts/BasicInfo/Department/DEP_BASIC/DEP_BASIC_Schema';
import { GON_BASIC_Schema } from '../Scripts/BasicInfo/Gongjong/GON_BASIC/GON_BASIC_Schema';

function RowsFromSearchResponse(res) {
  return handleQuerySuccess(res, {});
}

/**
 * 기본정보 4종 완료 여부 (조회 결과 행 기준)
 * @param {Array} plantRows GetPlaBasicR1
 * @param {Array} memberRows GetMemBasicR1 (행 1건 이상이면 완료)
 * @param {Array} depRows GetDepBasicR1
 * @param {boolean} hasAnyProcess 부서별 GetGonBasicR1 중 1건 이상
 */
export function EvaluateBasicInfoChecklist(
  plantRows,
  memberRows,
  depRows,
  hasAnyProcess
) {
  const workplaceRegistered =
    Array.isArray(plantRows) && plantRows.length > 0;
  const workforceRegistered =
    Array.isArray(memberRows) && memberRows.length > 0;
  const departmentRegistered =
    Array.isArray(depRows) && depRows.length > 0;
  const processRegistered = Boolean(hasAnyProcess);
  return {
    workplaceRegistered,
    workforceRegistered,
    departmentRegistered,
    processRegistered,
  };
}

const MAX_DEP_FOR_PROCESS_SCAN = 48;

/**
 * 신규 계약 가이드 — 기본정보 등록 완료 여부
 * - 사업장: 행 1건 이상
 * - 인력: 행 1건 이상
 * - 부서: 행 1건 이상
 * - 공정: 선택 사업장 부서 목록 기준, 부서당 공정 조회 중 코드 또는 명이 있는 행 1건 이상(부서 수 상한)
 */
export function useNewContractGuideChecklist(open) {
  const { userInfo } = useSelector((s) => s.BaseInfo);
  const search = useSelector((s) => s.SearchInfo);

  const ctxUser = useMemo(() => {
    if (!userInfo) return null;
    const coCd =
      userInfo.authCd === 'ADMIN'
        ? search?.coCd || userInfo.coCd || ''
        : userInfo.coCd || '';
    return { ...userInfo, coCd };
  }, [userInfo, search?.coCd]);

  const mapCtx = useMemo(
    () => (ctxUser && search ? { userInfo: ctxUser, search } : {}),
    [ctxUser, search]
  );

  const enabled =
    Boolean(open) &&
    Boolean(ctxUser?.userid) &&
    String(ctxUser?.coCd || '').trim().length > 0 &&
    String(search?.custCd || '').trim().length > 0;

  const qc = useQueries({
    queries: [
      {
        queryKey: [
          'NewContractGuideChecklist',
          'plant',
          ctxUser?.userid,
          ctxUser?.coCd,
        ],
        enabled,
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        queryFn: async () => {
          const params = mapParamsBySchema(
            PLA_BASIC_Schema.mapper.search1,
            mapCtx
          );
          const res = await apiRequest(
            'post',
            Global.urls.Plant.getPlaBasicR1,
            params
          );
          return RowsFromSearchResponse(res);
        },
      },
      {
        queryKey: [
          'NewContractGuideChecklist',
          'member',
          ctxUser?.userid,
          ctxUser?.coCd,
          search?.custCd,
        ],
        enabled,
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        queryFn: async () => {
          const params = mapParamsBySchema(
            MEM_BASIC_Schema.mapper.search1,
            mapCtx
          );
          const res = await apiRequest(
            'post',
            Global.urls.Member.getMemBasicR1,
            params
          );
          return RowsFromSearchResponse(res);
        },
      },
      {
        queryKey: [
          'NewContractGuideChecklist',
          'dep',
          ctxUser?.userid,
          ctxUser?.coCd,
          search?.custCd,
        ],
        enabled,
        staleTime: 30_000,
        refetchOnWindowFocus: false,
        queryFn: async () => {
          const params = mapParamsBySchema(
            DEP_BASIC_Schema.mapper.search1,
            mapCtx
          );
          const res = await apiRequest(
            'post',
            Global.urls.Depart.getDepBasicR1,
            params
          );
          return RowsFromSearchResponse(res);
        },
      },
    ],
  });

  const processQuery = useQuery({
    queryKey: [
      'NewContractGuideChecklist',
      'process',
      search?.custCd,
      ctxUser?.coCd,
    ],
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const depParams = mapParamsBySchema(
        GON_BASIC_Schema.mapper.search1,
        mapCtx
      );
      const depRes = await apiRequest(
        'post',
        Global.urls.Utility.getDepLast,
        depParams
      );
      const deps = RowsFromSearchResponse(depRes);
      if (!Array.isArray(deps) || deps.length === 0) return false;

      const depCds = [
        ...new Set(
          deps
            .map((d) => d?.depCd ?? d?.DepCd ?? '')
            .filter((cd) => String(cd).trim().length > 0)
        ),
      ].slice(0, MAX_DEP_FOR_PROCESS_SCAN);

      for (let i = 0; i < depCds.length; i += 1) {
        const depCd = depCds[i];
        const params = mapParamsBySchema(GON_BASIC_Schema.mapper.search2, {
          ...mapCtx,
          depCd,
        });
        const gRes = await apiRequest(
          'post',
          Global.urls.Gongjong.getGonBasicR1,
          params
        );
        const rows = RowsFromSearchResponse(gRes);
        const hasRow = Array.isArray(rows) && rows.length > 0;
        if (hasRow) return true;
      }
      return false;
    },
  });

  const plantRows = qc[0]?.data;
  const memberRows = qc[1]?.data;
  const depRows = qc[2]?.data;

  const checklist = useMemo(
    () =>
      EvaluateBasicInfoChecklist(
        plantRows ?? [],
        memberRows ?? [],
        depRows ?? [],
        processQuery.data
      ),
    [plantRows, memberRows, depRows, processQuery.data]
  );

  const isLoading =
    enabled &&
    (qc[0]?.isPending ||
      qc[1]?.isPending ||
      qc[2]?.isPending ||
      processQuery.isPending);

  const isError =
    qc[0]?.isError ||
    qc[1]?.isError ||
    qc[2]?.isError ||
    processQuery.isError;

  const refetch = () => {
    void qc[0]?.refetch();
    void qc[1]?.refetch();
    void qc[2]?.refetch();
    void processQuery.refetch();
  };

  return {
    checklist,
    enabled,
    isLoading,
    isError,
    refetch,
  };
}
